import { useMemo } from 'react';
import { useRecoilCallback, useRecoilValue } from 'recoil';
import { BroadcastChannel } from 'broadcast-channel';
import {
  DEFAULT_BYPASS,
  systemCheckPreferenceState,
  SystemCheckPreferenceState,
} from 'common/SystemCheck/useSystemCheck';
import { extractWorklistIds } from 'hooks/useWorklistId';
import { uniquePageId as id } from 'modules/activeWindow';
import { getPageType, PageTypes } from 'utils/pageTypes';
import { AppRoutePath } from '../config/constants';
import { extensionAppState, ExtTab, WindowState } from './useExtensionState';
import { ExtWindowName, Tab, OpenTabsInfo, computeFlags } from './useOpenTabsUtils';
import { isWardRoute } from 'domains/viewer/Viewer/Ward/utils';
import { getWindowId } from 'utils/page';

/** Returns the window name based on the URL path; returns null if unrecognized */
export const getWindowName = (pathname: string): ExtWindowName | null => {
  // Helper to match a path pattern (with :params) to a pathname
  const matchRoute = (route: string) => {
    const regex = new RegExp(
      route
        // Convert route params into wildcard matcher
        .replace(/:[A-Za-z]+/g, '.+')
        // Escape slashes for regex
        .replace(/\//g, '\\/')
        // Truncate query params
        .replace(/\?.+/g, '')
    );
    return regex.test(pathname);
  };

  if (matchRoute(AppRoutePath.VIEWER) || matchRoute(AppRoutePath.VIEWER_BLANK)) {
    // Extract windowId from the pathname
    const viewerWindowId = getWindowId(pathname);
    if (viewerWindowId === '0') return ExtWindowName.Viewer0;
    if (viewerWindowId === '1') return ExtWindowName.Viewer1;
    return ExtWindowName.Viewer0; // Default to Viewer0 if no windowId is found
  }
  if (matchRoute(AppRoutePath.REPORTER) || matchRoute(AppRoutePath.REPORTER_BLANK))
    return ExtWindowName.Reporter;
  if (
    matchRoute(AppRoutePath.PATIENT_JACKET) ||
    matchRoute(AppRoutePath.PATIENT_JACKET_UNDOCKED) ||
    matchRoute(AppRoutePath.PATIENT_JACKET_BLANK)
  )
    return ExtWindowName.Patient_Jacket;
  if (matchRoute(AppRoutePath.AI_SANDBOX) || matchRoute(AppRoutePath.AI_SANDBOX_BLANK))
    return ExtWindowName.AISandbox;
  if (matchRoute(AppRoutePath.WORKLIST)) return ExtWindowName.Worklist;
  return null;
};

export type PageType = {
  type: (typeof PageTypes)[keyof typeof PageTypes];
  id: string;
  createTimestamp: number;
  timestamp: number;
  windowId: Nullable<string>;
  url: string;
  worklistIds: Array<string>;
};

// Constant time for when the page was originally created, even when
// refreshing the timestamp to maintain active tab states it will have a
// property to allow sorting by when they were created.
const creationTimestamp = Date.now();

export const getPage = (): PageType => ({
  type: getPageType(window.location.href),
  id,
  createTimestamp: creationTimestamp,
  timestamp: Date.now(),
  windowId: getWindowId(window.location.href),
  url: window.location.href,
  worklistIds: extractWorklistIds(window.location.pathname),
});

export type Message =
  | {
      type: 'register' | 'unregister';
      payload: PageType;
    }
  | {
      type: 'ping';
    };
export const bc: BroadcastChannel<Message> = new BroadcastChannel<Message>('use-tab-list');

const register = () => bc.postMessage({ type: 'register', payload: getPage() });
register();

window.addEventListener('beforeunload', () => {
  bc.postMessage({ type: 'unregister', payload: getPage() });
});

const WINDOW_NAME_TO_ID = {
  viewer0: '0',
  viewer1: '1',
  reporter: undefined,
  worklist: undefined,
  'patient-jacket': undefined,
  'ai-sandbox': undefined,
} as const;

/**
 * Creates an artificial `Tab` object to inject into the hook when the extension is not detected.
 * The source of truth for tabs should come from the extension; this should only be used as a last
 * resort fallback for system check bypasses and other edge cases like supporting the Vision Pro.
 */
const createArtificialTab = (type: PageTypes): Tab => ({
  type,
  windowId: type === PageTypes.VIEWER ? '0' : '1',
  url: window.location.href,
  path: window.location.pathname,
  worklistIds: extractWorklistIds(window.location.pathname),
});

export const computeOpenTabs = (
  extensionState: WindowState,
  systemCheckPreference: SystemCheckPreferenceState
): OpenTabsInfo => {
  const tabList: Array<Tab> = [];
  const extensionReportsNoOpenWindows = Object.entries(extensionState?.windows ?? {}).length === 0;
  if (extensionReportsNoOpenWindows) {
    if (systemCheckPreference?.bypass?.extension) {
      // At this point the extension has not been detected and the extension portion of the system
      // check has been bypassed, so we should return an artificial tab to enable the downstream
      // logic on the viewer to render something.

      // Identify the window
      const pathname = window.location.pathname;
      const type = pathname.includes('ai-sandbox')
        ? PageTypes.AI_SANDBOX
        : pathname.includes('viewer')
          ? PageTypes.VIEWER
          : PageTypes.WORKLIST;

      const artificialTabs = [createArtificialTab(type)];
      return computeFlags(artificialTabs);
    } else if (isWardRoute()) {
      const artificialTabs = [createArtificialTab(PageTypes.VIEWER)];
      return computeFlags(artificialTabs);
    }
    return computeFlags([]);
  }

  Object.entries(extensionState.windows).forEach(([windowName, tab]: [string, ExtTab | null]) => {
    if (tab == null) return;

    let windowId: Nullable<string> = undefined;
    windowId = WINDOW_NAME_TO_ID[windowName as ExtWindowName];

    // Maps ExtWindowName to PageTypes, with the only difference being that PageTypes doesn't
    // distinguish between viewer0 and viewer1. In the future we should consider merging these two
    // enums.
    let type: PageTypes = PageTypes.WORKLIST;
    if (windowName === ExtWindowName.Reporter) {
      type = PageTypes.REPORTER;
    } else if (windowName === ExtWindowName.AISandbox) {
      type = PageTypes.AI_SANDBOX;
    } else if (windowName === ExtWindowName.Patient_Jacket) {
      type = PageTypes.PATIENT_JACKET;
    } else if (windowName === ExtWindowName.Viewer0 || windowName === ExtWindowName.Viewer1) {
      type = PageTypes.VIEWER;
    }

    tabList.push({
      type,
      windowId,
      url: tab.url,
      path: tab.path,
      worklistIds: extractWorklistIds(tab.path),
    });
  });

  const sortedTabs = tabList.filter(Boolean).sort((a, b) => {
    if (a.windowId != null && b.windowId != null) return a.windowId.localeCompare(b.windowId);
    return -1;
  });
  return computeFlags(sortedTabs);
};

export const useOpenTabs = (): OpenTabsInfo => {
  const extensionState = useRecoilValue(extensionAppState);
  const systemCheckPreference = useRecoilValue(systemCheckPreferenceState);

  const info: OpenTabsInfo = useMemo(
    () => computeOpenTabs(extensionState, systemCheckPreference),
    [extensionState, systemCheckPreference]
  );

  return info;
};

// Returns a stable callback that reads open-tab state without subscribing
// the caller to tab updates. Useful in hot paths where render churn matters.
export const useGetOpenTabsInfo = (): (() => OpenTabsInfo) => {
  return useRecoilCallback(
    ({ snapshot }) =>
      () => {
        const extensionState = snapshot.getLoadable(extensionAppState).valueMaybe() ?? {
          windows: {},
        };
        const systemCheckPreference = snapshot
          .getLoadable(systemCheckPreferenceState)
          .valueMaybe() ?? { bypass: DEFAULT_BYPASS };

        return computeOpenTabs(extensionState, systemCheckPreference);
      },
    []
  );
};

export const crossTabBroadcastChannel: BroadcastChannel<
  | {
      type: 'request' | 'response';
      url: string;
      id?: string;
    }
  | {
      type: 'setSize';
      tabId: string;
      width?: number;
      height?: number;
      top?: number;
      left?: number;
    }
> = new BroadcastChannel('cross-tab-controls');

// performance.timing is deprecated; some environments (e.g. jsdom) leave
// navigationStart undefined, which makes the subtraction produce NaN. Fall
// back to 0 so CHECK_TIMEOUT stays a valid number in all environments.
const rawTimeToLoad =
  window.performance.timing.domContentLoadedEventEnd - window.performance.timing.navigationStart;
const TIME_TO_LOAD = Number.isFinite(rawTimeToLoad) ? rawTimeToLoad : 0;

// Highest value between 3s and the (time it took to load the current page + 1s)
const CHECK_TIMEOUT = Math.max(3000, TIME_TO_LOAD + 1000);

/**
 * The below behemoth is required to detect if a popup has been blocked
 * by an ad blocker so that we can inform the user about it.
 *
 * The system is actually quite simple, every page listens to a specific
 * broadcast channel event, when the event is received, the page will
 * check if the event is looking for that specific page, and if it is,
 * it will send a message back to the page that requested the check.
 */
export const checkTabPresence = (url: string): Promise<Nullable<string>> => {
  return new Promise(
    // eslint-disable-next-line no-async-promise-executor
    async (resolve: (result?: Nullable<Promise<Nullable<string>> | string>) => void) => {
      let aborted = false;

      // This will fire after 1 second if no `response` event is received
      const timeout = setTimeout(() => {
        aborted = true;
        resolve(null);
      }, CHECK_TIMEOUT);

      // If a `response` event is received and the URL matches, we can
      // assume that the tab is open and we can resolve the promise
      // with the tab ID
      const callback = async (
        event:
          | {
              id?: string;
              type: 'request' | 'response';
              url: string;
            }
          | {
              height?: number;
              left?: number;
              tabId: string;
              top?: number;
              type: 'setSize';
              width?: number;
            }
      ) => {
        if (event.type === 'response' && event.url === url) {
          aborted = true;

          // If we receive a response we cancel the above timer and resolve the promise positively
          clearTimeout(timeout);
          resolve(event.id);
        }
      };
      crossTabBroadcastChannel.addEventListener('message', callback);

      // We send a request to the broadcast channel, this will trigger
      // the callback above if a tab is open with the same URL
      // We retry every 100ms until we either receive a response or
      // the timeout is reached
      while (aborted === false) {
        crossTabBroadcastChannel.postMessage({ type: 'request', url, id });
        await new Promise((resolve: (result: Promise<undefined> | undefined) => void) =>
          setTimeout(resolve, 100)
        );
      }

      // We remove the callback from the broadcast channel
      crossTabBroadcastChannel.removeEventListener('message', callback);
    }
  );
};

/**
 * The following event listener is used to make the `checkTabPresence`
 * and `setTabSize` work.
 */
crossTabBroadcastChannel.addEventListener('message', (event) => {
  switch (event.type) {
    case 'request': {
      if (event.url === window.location.pathname) {
        crossTabBroadcastChannel.postMessage({
          type: 'response',
          url: event.url,
          id,
        });
      }
      break;
    }
    case 'setSize': {
      if (event.tabId === id) {
        if (event.width != null && event.height != null) {
          // This is the height of the tabs bar + address bar + bookmarks bar
          const browserInterfaceHeight = window.outerHeight - window.innerHeight;
          window.resizeTo(event.width, event.height + browserInterfaceHeight);
        }
        if (event.top != null && event.left != null) {
          window.moveTo(event.left, event.top);
        }
      }
      break;
    }
    default:
  }
});
